import { FC, Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MdVolumeUp, MdVolumeOff } from 'react-icons/md';
import { Motion, spring } from 'react-motion';
// Components
import { Button } from 'components/Buttons';
import VideoTimeline from './VideoTimeline';
// Icons
import { IoIosSkipForward } from "react-icons/io";
// i18next
import { useTranslation } from 'react-i18next';

type Props = {
  isQuestionVideo?: boolean;
  videoClasses?: string;
  videoUrl: string;
  onLoadCallback?: () => void;
  onEndedCallback?: () => void;
}

const rootVideoElem = document.getElementById('rootVideo') as HTMLVideoElement;

const Video:FC<Props> = ({
  // Props
  videoClasses = 'w-full', videoUrl, onLoadCallback, onEndedCallback, isQuestionVideo
}) => {
  const { t } = useTranslation();
  const timeout = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [ isMuted, setIsMuted ] = useState(false);
  const [ duration, setDuration ] = useState<number>(0);
  const [ skipCountdown, setSkipCountdown ] = useState<number>(3);

  const onEnded = () => {
    setIsMuted(false);
    if ( onEndedCallback ) onEndedCallback();
    setDuration(0);
    setSkipCountdown(3);
  }

  const skipVideo = () => {
    if ( rootVideoElem ) {
      rootVideoElem.pause();
    }
    onEnded();
  }

  useLayoutEffect(() => {
    const container = containerRef.current;
  
    const sourceElem = document.createElement('source');

    sourceElem.onerror = (e) => {
      console.log('[video] Source error loading video', e);
      onEnded();
    };

    sourceElem.src = videoUrl;
  
    rootVideoElem.className = videoClasses;
    rootVideoElem.appendChild(sourceElem);
  
    rootVideoElem.muted = false;

    rootVideoElem.load();
  
    if ( onLoadCallback ) rootVideoElem.onload = onLoadCallback;
  
    const promise = rootVideoElem.play();
  
    if ( typeof promise !== 'undefined' ){
      promise
        .catch((error) => {
          if (error.name === 'NotAllowedError') {
            console.info('[video] Autoplay blocked, muting and retrying...', error);
          } else {
            console.warn('[video] Unexpected video play error:', error);
          }
  
          rootVideoElem.muted = true;
          setIsMuted(true);
        })
        .finally(() => {
          if ( container ) container.appendChild(rootVideoElem);
  
          timeout.current = setTimeout(() => {
            rootVideoElem.play().catch((e) => {
              console.warn('[video] Retry play failed, skipping...', e);
              onEnded();
            });
          }, 100);
        })
    }
  
    rootVideoElem.onended = onEnded;
  
    return () => {
      if ( timeout.current ) clearTimeout(timeout.current);
      if (container && rootVideoElem && container.contains(rootVideoElem)) {
        container.removeChild(rootVideoElem);
      }
  
      rootVideoElem.removeChild(sourceElem);
      rootVideoElem.className = 'hidden';
  
      document.body.appendChild(rootVideoElem);
    }
    // eslint-disable-next-line
  }, []);

  const toggleMute = () => {
    rootVideoElem.muted = !isMuted;

    setIsMuted(prev => !prev);
  }

  useLayoutEffect(() => {
    if (!isQuestionVideo) return;

    if (rootVideoElem) {
      // for some reason on iOS 16.3+ the video does not start from the beginning
      rootVideoElem.currentTime = 0.01;
      rootVideoElem.addEventListener('timeupdate', updateCountdown);
    }

    return () => {
      rootVideoElem.removeEventListener('timeupdate', updateCountdown);
    }
    // eslint-disable-next-line
  }, [rootVideoElem])

  function updateCountdown() {
    setDuration(rootVideoElem.currentTime / rootVideoElem.duration * 100);
  }

  useEffect(() => {
    if (isQuestionVideo) return;

    setSkipCountdown(3);
    const interval = setInterval(() => {
      setSkipCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isQuestionVideo]);

  return (
    <Fragment>
      <div className="absolute left-0 top-0 m-4 z-10">
        <Button
          onClick={toggleMute}
          disableShadow
          size="small"
        >
          {isMuted ? <MdVolumeOff /> : <MdVolumeUp />}
        </Button>
      </div>
      {isQuestionVideo ? (
        <div className="bg-black relative w-full pt-56.25%">
          <div className="absolute top-0 left-0 w-full h-full" ref={containerRef} />
          {!!duration && (
            <Motion style={{ smoothProgress: spring(duration) }}>
              {values => <VideoTimeline progress={values.smoothProgress} /> }
            </Motion>
          )}
        </div>
      ) : (
        <div className="bg-black w-full" ref={containerRef} />
      )}
      {!isQuestionVideo && (
        <div className="absolute right-0 bottom-0 m-4 z-10">
          <Button
            onClick={skipVideo}
            disableShadow
            size="small"
            disabled={skipCountdown > 0}
            className="primary-button flex items-center gap-1"
          >
            {skipCountdown > 0 ? `${skipCountdown} ${t('buttons.skip')}` : t('buttons.skip')}
            <IoIosSkipForward />
          </Button>
        </div>
      )}
    </Fragment>
  )
}

export default Video;
